<template>
	<div class="client-invoices-list" :aria-label="listTitle">

		<div class="invoice-controls" v-if="currentList">
			<div class="action-bar">
				<button class="action-btn" type="button" @click="handleCreateInvoice">
					<AppointmentIcon name="plus" />
					<span>New invoice</span>
				</button>
				<button
					class="action-btn"
					type="button"
					:disabled="isExporting || isLoading"
					@click="exportInvoicesTable"
				>
					<AppointmentIcon name="export" />
					<span>{{ isExporting ? 'Exporting…' : 'Export' }}</span>
				</button>

				<template v-if="hasSelection">
					<span class="action-bar-divider" aria-hidden="true"></span>
					<button
						class="action-btn"
						type="button"
						:disabled="isGeneratingDocument || isBatchDownloading"
						@click="handleDownloadSelected"
					>
						<BsDownload class="action-btn-icon" />
						<span>{{ isBatchDownloading ? 'Downloading…' : `Download${selectedCount ? ` (${selectedCount})` : ''}` }}</span>
					</button>
					<button
						class="action-btn action-btn--muted"
						type="button"
						:disabled="isGeneratingDocument || isBatchDownloading"
						@click="clearSelection"
					>
					<BsXLg class="action-btn-icon" />
					<span>Deselect all</span>
					</button>
				</template>
			</div>

			<section class="filters-bar" aria-label="Invoice filters">
				<div class="filters-bar-start">
					<AppointmentIcon name="filter" />
				</div>

				<!-- Invoice # -->
				<FilterMotion :expanded="editingFilterType === 'search'">
					<div v-if="filters.search && editingFilterType !== 'search'" class="filter-chip">
						<span class="filter-chip-text"># {{ filters.search }}</span>
						<button class="filter-chip-remove" type="button" aria-label="Clear invoice number filter" @click.stop="clearSearchFilter">
							<AppointmentIcon name="close" :size="14" />
						</button>
					</div>
					<div v-else-if="editingFilterType === 'search'" class="filter-inline-editor" @click.stop>
						<input
							ref="searchFilterRef"
							type="text"
							class="filter-editor-input"
							placeholder="Invoice #..."
							v-model.trim="filters.search"
							@input="debouncedReload"
							@keydown.enter="editingFilterType = null"
							@keydown.tab="editingFilterType = null"
						/>
					</div>
					<button v-else class="filter-label-btn" type="button" @click.stop="startFilterEdit('search')">
						Invoice #
					</button>
				</FilterMotion>

				<!-- Client -->
				<FilterMotion :expanded="editingFilterType === 'client' && !filters.clientId">
					<div v-if="filters.clientId" class="filter-chip">
						<span class="filter-chip-text">{{ resolvedClientName }}</span>
						<button class="filter-chip-remove" type="button" aria-label="Remove client filter" @click.stop="handleCatalogFilterChange('clientId', null)">
							<AppointmentIcon name="close" :size="14" />
						</button>
					</div>
					<div v-else-if="editingFilterType === 'client'" class="filter-inline-editor" @click.stop>
						<CatalogSearchInput use-fixed
							ref="clientFilterRef"
							:modelValue="filters.clientId"
							@update:modelValue="(val) => handleFilterValue('clientId', val)"
							:options="clientOptions"
							placeholder="Search client..."
							class="filter-editor-search"
						/>
					</div>
					<button v-else class="filter-label-btn" type="button" @click.stop="startFilterEdit('client')">
						Client
					</button>
				</FilterMotion>

				<!-- Building -->
				<FilterMotion :expanded="editingFilterType === 'building' && !filters.buildingId">
					<div v-if="filters.buildingId" class="filter-chip">
						<span class="filter-chip-text">{{ resolvedBuildingName }}</span>
						<button class="filter-chip-remove" type="button" aria-label="Remove building filter" @click.stop="handleCatalogFilterChange('buildingId', null)">
							<AppointmentIcon name="close" :size="14" />
						</button>
					</div>
					<div v-else-if="editingFilterType === 'building'" class="filter-inline-editor" @click.stop>
						<CatalogSearchInput use-fixed
							ref="buildingFilterRef"
							:modelValue="filters.buildingId"
							@update:modelValue="(val) => handleFilterValue('buildingId', val)"
							:options="buildingOptions"
							placeholder="Search building..."
							class="filter-editor-search"
						/>
					</div>
					<button v-else class="filter-label-btn" type="button" @click.stop="startFilterEdit('building')">
						Building
					</button>
				</FilterMotion>

				<!-- Status -->
				<div v-if="filters.status !== 'all'" class="filter-chip">
					<span class="filter-chip-text">{{ filters.status === 'paid' ? 'Paid' : 'Unpaid' }}</span>
					<button class="filter-chip-remove" type="button" aria-label="Clear status filter" @click.stop="resetStatusFilter">
						<AppointmentIcon name="close" :size="14" />
					</button>
				</div>
				<div v-else class="status-dropdown-wrapper" @keydown.esc="editingFilterType = null">
					<button class="filter-label-btn" type="button" aria-haspopup="true" :aria-expanded="editingFilterType === 'status'" @click.stop="editingFilterType = editingFilterType === 'status' ? null : 'status'">Status</button>
					<template v-if="editingFilterType === 'status'">
						<div class="status-dropdown">
							<button type="button" class="status-dropdown-option" @click="selectStatus('unpaid')">Unpaid</button>
							<button type="button" class="status-dropdown-option" @click="selectStatus('paid')">Paid</button>
						</div>
					</template>
				</div>

				<!-- Date range -->
				<div :class="['filter-date-wrapper', { 'has-value': filters.dateFrom || filters.dateTo }]">
					<DateRangePicker use-fixed
						:show-trigger-icon="false"
						:dateFrom="filters.dateFrom"
						:dateTo="filters.dateTo"
						placeholder="Date range"
						@update="handleDateRangePickerUpdate"
					>
						<template #previous-icon><AppointmentIcon name="chevron-left" /></template>
						<template #next-icon><AppointmentIcon name="chevron-right" /></template>
					</DateRangePicker>
					<button v-if="filters.dateFrom || filters.dateTo" class="filter-chip-remove" type="button" aria-label="Clear date range" @click.stop="clearDateFilter">
						<AppointmentIcon name="close" :size="14" />
					</button>
				</div>
			</section>
		</div>

		<div v-if="!currentList" class="empty-state">
			<p>No invoice list available.</p>
			<p class="hint">Please create a client invoices list in Strapi.</p>
		</div>

		<div v-else class="content-wrapper">
			<div class="table-wrapper">
				<div v-if="showLoading" class="loading-state">Loading invoices…</div>
				<div v-else-if="itemsError" class="error-state">
					{{ itemsError }}
					<button class="link-btn" @click="reloadItems">Retry</button>
				</div>
				<div v-else class="invoice-list-container">
					<table class="invoice-table">
						<thead>
							<tr class="table-header-row">
								<th class="col-select"></th>
								<th class="col-number">Invoice #</th>
								<th class="col-date">Date</th>
								<th class="col-client">Client</th>
								<th class="col-building">Building</th>
								<th class="col-due">Due date</th>
								<th class="col-total">Total</th>
								<th class="col-status">Status</th>
							</tr>
						</thead>
						<tbody>
							<tr v-if="!items.length">
								<td colspan="8" class="empty-cell">No invoices found for current filters.</td>
							</tr>
							<tr
								v-for="item in items"
								:key="item.id"
								class="table-row"
								:class="{ 
									locked: (item.invoiceStatus || 'unpaid') === 'paid',
									active: activeSubmenuItemId === item.id,
									'status-editor-open': statusEditorOpenItemId === item.id
								}"
								@click="toggleSubmenu(item, $event)"
								@contextmenu.prevent="openSubmenu(item, $event)"
							>
								<td class="col-select" @click.stop="toggleInvoiceSelection(item)">
									<input
										type="checkbox"
										class="row-select-checkbox"
										:aria-label="`Select invoice ${item.invoiceNumber || item.id}`"
										:checked="isInvoiceSelected(item)"
										:disabled="isGeneratingDocument || isBatchDownloading"
										@click.stop
										@change="toggleInvoiceSelection(item)"
									/>
								</td>
								<td class="col-number">{{ item.invoiceNumber || '—' }}</td>
								<td class="col-date">{{ formatDate(item.invoiceDate) }}</td>
								<td class="col-client">{{ getClientName(item) }}</td>
								<td class="col-building" v-tooltip>{{ getBuildingName(item) }}</td>
								<td class="col-due">{{ formatDate(item.dueDate) }}</td>
								<td class="col-total numeric">{{ formatCurrency(resolveInvoiceTotal(item)) }}</td>
								<td
									class="col-status status-cell-wrapper"
									:class="{ 'is-open': statusEditorOpenItemId === item.id }"
									@click.stop
								>
									<InvoiceStatusCell
										use-fixed
										:item-id="item.id"
										:status="item.invoiceStatus || 'unpaid'"
										:paid-date="getPaidDate(item)"
										:loading="statusUpdatingId === item.id"
										@submit="payload => handleStatusUpdate(item, payload)"
										@open="() => { statusEditorOpenItemId = item.id; closeSubmenu(); }"
										@close="statusEditorOpenItemId = null"
									/>
								</td>
							</tr>
						</tbody>
					</table>

					<div v-if="hasActiveFilters && items.length" class="invoice-total-row">
						<div class="total-cell col-select items-count">{{ items.length }} items</div>
						<div class="total-cell col-number"></div>
						<div class="total-cell col-date"></div>
						<div class="total-cell col-client"></div>
						<div class="total-cell col-building"></div>
						<div class="total-cell col-due"></div>
						<div class="total-cell col-total">Total: {{ formattedFilteredTotal }}</div>
						<div class="total-cell col-status"></div>
					</div>

					<!-- Submenu -->
					<Teleport to="body">
						<div
							v-if="activeSubmenuItemId !== null"
							ref="submenuRef"
							class="submenu invoice-row-submenu"
							:style="submenuStyle"
							@click.stop
							@keydown.esc="closeSubmenu"
						>
							<button @click.stop="handleEditSubmenuItem" class="submenu-btn">
								{{ isCreditNoteItem(activeSubmenuItem) ? 'Edit credit note' : 'Edit invoice' }}
							</button>

							<button
								v-if="!isCreditNoteItem(activeSubmenuItem)"
								@click.stop="handleCreateCreditNoteSubmenuItem"
								class="submenu-btn"
							>
								Create credit note
							</button>

							<button @click.stop="handlePreviewSubmenuItem" class="submenu-btn">
								Preview invoice
							</button>

							<button @click.stop="handleDownloadSubmenuItem" class="submenu-btn">
								Download PDF
							</button>

							<button
								v-if="allowHardDelete"
								@click.stop="handleDeleteSubmenuItem"
								class="submenu-btn delete"
							>
								Delete Invoice
							</button>
						</div>
					</Teleport>
				</div>
				<div
					v-if="showTableLoadingOverlay"
					class="table-loading-overlay"
					role="status"
					aria-live="polite"
				>
					<div class="table-loading-indicator">
						<span class="table-loading-spinner" aria-hidden="true"></span>
						<span>Loading invoices…</span>
					</div>
				</div>
			</div>


			<footer class="pagination-bar" aria-label="Invoice pagination" v-if="(pagination.pageCount > 1 || pagination.total) && !hasActiveFilters">
				<div class="pagination-controls">
					<button type="button" class="pagination-btn prev" aria-label="Previous page" :disabled="pagination.page === 1" @click="changePage(pagination.page - 1)">
						<AppointmentIcon name="chevron-left" />
					</button>
					
					<template v-for="(p, index) in visiblePages" :key="index">
						<span v-if="p === '...'" class="pagination-ellipsis">...</span>
						<button v-else type="button" class="pagination-btn page-number" :aria-label="`Page ${p}`" :aria-current="p === pagination.page ? 'page' : undefined" :class="{ active: p === pagination.page }" @click="changePage(p)">
							{{ p }}
						</button>
					</template>

					<button
						type="button"
						class="pagination-btn next"
						aria-label="Next page"
						:disabled="pagination.page === pagination.pageCount"
						@click="changePage(pagination.page + 1)"
					>
						<AppointmentIcon name="chevron-right" />
					</button>
				</div>
				<div class="pagination-results">
					Results: {{ resultsStart }} - {{ resultsEnd }} of {{ pagination.total }}
				</div>
			</footer>
		</div>

		<div v-if="allowHardDelete && showDeleteModal" class="modal-overlay" @click="cancelDelete">
			<div class="modal-content" @click.stop>
				<h3 class="modal-title">Delete Invoice</h3>
				<p class="modal-message">
					Are you sure you want to delete
					{{ deleteInvoiceLabel || 'this invoice' }}?
					This action cannot be undone.
				</p>
				<div class="modal-actions">
					<button class="modal-btn cancel" @click="cancelDelete">
						Cancel
					</button>
					<button
						class="modal-btn confirm"
						@click="confirmDelete"
						:disabled="isDeletingInvoice"
					>
						{{ isDeletingInvoice ? 'Deleting...' : 'Delete' }}
					</button>
				</div>
			</div>
		</div>

		<InvoicePreview
			:show-preview="showPreviewModal"
			:invoice-html="previewHtml"
			@close-preview="closePreviewModal"
			@download-pdf="downloadPreviewPdf"
		/>
	</div>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount, onDeactivated, reactive, ref, watch, nextTick, getCurrentInstance } from 'vue';
import { fitFloatingPanel } from '@/utils/floatingPanel';
import debounce from '@/utils/debounce';
import { useClientInvoicesStore } from '@/stores/clientInvoices';
import { useCatalogStore } from '@/stores/catalog';
import { useProjectsStore } from '@/stores/projects';
import { useMediaStore } from '@/stores/media';
import { generateInvoiceHtmlWithAutoSpacing, buildBusinessInfo, deriveInvoiceFilename } from '@/utils/invoiceDocument';
import { convertImageToDataUrl } from '@/utils/imageUtils';
import CatalogSearchInput from '@/components/CatalogSearchInput.vue';
import DateRangePicker from '@/components/DateRangePicker.vue';
import InvoiceStatusCell from '@/components/invoices/InvoiceStatusCell.vue';
import InvoicePreview from '@/components/invoices/InvoicePreview.vue';
import AppointmentIcon from '@/components/icons/AppointmentIcon.vue';
import FilterMotion from '@/components/journal/FilterMotion.vue';
import { emitEvent } from '@/utils/eventBus';
import { waitForClonedDocumentFonts } from '@/utils/clientDocumentTheme';
import { sameRecords, useLiveRefresh } from '@/composables/useLiveRefresh';
import { BsDownload, BsXLg } from '@kalimahapps/vue-icons';
import { INVOICE_CURRENCY } from '@/constants/invoiceDefaults';

const emit = defineEmits(['open-editor', 'open-invoice']);

const props = defineProps({
	listId: {
		type: [String, Number],
		default: null,
	},
	documentId: {
		type: [String, Number],
		default: null,
	},
	draftId: {
		type: [String, Number],
		default: null,
	},
	listTitle: {
		type: String,
		default: 'Client Invoices',
	},
});

const clientInvoicesStore = useClientInvoicesStore();
const catalogStore = useCatalogStore();
const projectsStore = useProjectsStore();
const mediaStore = useMediaStore();

const toId = (value) => {
	if (value === null || value === undefined) return null;
	return String(value);
};

const listId = ref(toId(props.listId));
const allowHardDelete = true;
const filters = reactive({
	search: '',
	status: 'all',
	dateFrom: '',
	dateTo: '',
	clientId: null,
	buildingId: null,
});
const syncingFilters = ref(false);
const storeReady = ref(false);
const currentPage = ref(1);
const isExporting = ref(false);
const selectedInvoiceIds = ref([]);
const isBatchDownloading = ref(false);

// Submenu state
const activeSubmenuItemId = ref(null);
const submenuRef = ref(null);
const submenuStyle = ref({});
let submenuPoint = { left: 0, top: 0, bottom: 0 };
const isGeneratingDocument = ref(false);
const showPreviewModal = ref(false);
const previewHtml = ref('');
const previewInvoiceData = ref(null);
const showDeleteModal = ref(false);
const deleteTarget = ref(null);
const isDeletingInvoice = ref(false);

const { proxy } = getCurrentInstance();
const toast = proxy?.$toast;
const logError = proxy?.$logError;

const serializeFilters = () => {
	const base = {
		search: filters.search || '',
		status: filters.status || 'all',
		dateFrom: filters.dateFrom || '',
		dateTo: filters.dateTo || '',
		clientId: filters.clientId || null,
		buildingId: filters.buildingId || null,
	};

	// If a range was entered with reversed boundaries, correct it here before hitting the API.
	if (base.dateFrom && base.dateTo) {
		const fromDate = new Date(base.dateFrom);
		const toDate = new Date(base.dateTo);
		if (
			!Number.isNaN(fromDate.getTime()) &&
			!Number.isNaN(toDate.getTime()) &&
			fromDate > toDate
		) {
			[base.dateFrom, base.dateTo] = [base.dateTo, base.dateFrom];
		}
	}

	return base;
};

const hasActiveFilters = computed(() => {
	const normalized = serializeFilters();
	return Boolean(
		(normalized.search || '').trim() ||
		normalized.status !== 'all' ||
		normalized.dateFrom ||
		normalized.dateTo ||
		normalized.clientId ||
		normalized.buildingId,
	);
});

const hydrateFiltersFromStore = () => {
	if (!listId.value) return;
	const saved = clientInvoicesStore.getActiveFilters(listId.value);
	syncingFilters.value = true;
	filters.search = saved.search || '';
	filters.status = saved.status || 'all';
	filters.dateFrom = saved.dateFrom || '';
	filters.dateTo = saved.dateTo || '';
	filters.clientId = saved.clientId || null;
	filters.buildingId = saved.buildingId || null;
	syncingFilters.value = false;
};

const persistFiltersForCurrentList = () => {
	if (!listId.value) return;
	clientInvoicesStore.setActiveFilters(listId.value, serializeFilters());
};

const bootstrapCurrentList = async () => {
	if (!storeReady.value || !listId.value) return;
	hydrateFiltersFromStore();
	clientInvoicesStore.setActiveList(listId.value);
	persistFiltersForCurrentList();
	await reloadItems();
};

const isLoading = computed(
	() => clientInvoicesStore.itemsLoadingByListId[listId.value] || false,
);
const itemsError = computed(
	() => clientInvoicesStore.itemsErrorByListId[listId.value] || null,
);
const items = computed(() => clientInvoicesStore.itemsByListId[listId.value] || []);
const activeSubmenuItem = computed(() =>
	items.value.find((item) => item.id === activeSubmenuItemId.value) || null,
);
const hasLoadedItems = computed(() => {
	const key = listId.value;
	if (!key) return false;
	return Boolean(clientInvoicesStore.paginationByListId[key]);
});
const showLoading = computed(() => isLoading.value && !hasLoadedItems.value);
const showTableLoadingOverlay = computed(
	() => isLoading.value && hasLoadedItems.value && !itemsError.value,
);
const selectedCount = computed(() => selectedInvoiceIds.value.length);
const hasSelection = computed(() => selectedCount.value > 0);
const clientOptions = computed(() => catalogStore.clientOptions || []);
const buildingOptions = computed(() => catalogStore.optionsByCatalogName?.("Clients’ Buildings") || []);
const pagination = computed(
	() =>
		clientInvoicesStore.paginationByListId[listId.value] || {
			page: 1,
			pageCount: 1,
			pageSize: 0,
			total: 0,
		},
);
const currentList = computed(() =>
	clientInvoicesStore.lists.find((entry) => entry.id === listId.value) || null,
);
const projectInfoData = computed(() => {
	return buildBusinessInfo(projectsStore.activeProject);
});
const statusUpdatingId = ref(null);
const statusEditorOpenItemId = ref(null);

const getBusinessLogoUrl = async () => {
	let logoUrl = null;
	if (projectsStore.activeProject?.logo?.id) {
		try {
			const signed = await mediaStore.getSignedUrl(projectsStore.activeProject.logo.id);
			logoUrl = signed.url;
		} catch (error) {
			logError(error);
		}
	}
	// Fallback to stored URL if signing fails or no id (e.g., already public URL)
	if (!logoUrl && projectsStore.activeProject?.logo?.url) {
		logoUrl = projectsStore.activeProject.logo.url;
	}
	if (logoUrl) {
		const dataUrl = await convertImageToDataUrl(logoUrl);
		return dataUrl || logoUrl;
	}
	return null;
};

const closePreviewModal = () => {
	showPreviewModal.value = false;
	previewHtml.value = '';
	previewInvoiceData.value = null;
};

const openPreviewModal = (invoice, html) => {
	previewInvoiceData.value = invoice;
	previewHtml.value = html;
	showPreviewModal.value = true;
};

const downloadPreviewPdf = async () => {
	if (!previewInvoiceData.value || !previewHtml.value || isGeneratingDocument.value) return;
	try {
		isGeneratingDocument.value = true;
		const filename = deriveInvoiceFilename(previewInvoiceData.value);
		await downloadInvoicePdfFromHtml({ html: previewHtml.value, filename });
	} catch (error) {
		logError(error);
	} finally {
		isGeneratingDocument.value = false;
	}
};

const handleDateRangePickerUpdate = ({ dateFrom, dateTo }) => {
	filters.dateFrom = dateFrom || '';
	filters.dateTo = dateTo || '';
	reloadItems();
};

const clearDateFilter = () => {
	filters.dateFrom = '';
	filters.dateTo = '';
	reloadItems();
};

const handleStatusFilterChange = () => {
	reloadItems();
};

const resetStatusFilter = () => {
	if (filters.status === 'all') return;
	filters.status = 'all';
	reloadItems();
};

const debouncedReload = debounce(() => reloadItems(), 300);

// Inline filter editing
const editingFilterType = ref(null);
const searchFilterRef = ref(null);
const clientFilterRef = ref(null);
const buildingFilterRef = ref(null);
const statusFilterRef = ref(null);

const resolvedClientName = computed(() => {
	if (!filters.clientId) return '';
	const opt = clientOptions.value?.find(o => String(o.id) === String(filters.clientId));
	return opt?.label || opt?.name || `Client #${filters.clientId}`;
});

const resolvedBuildingName = computed(() => {
	if (!filters.buildingId) return '';
	const opt = buildingOptions.value?.find(o => String(o.id) === String(filters.buildingId));
	return opt?.label || opt?.name || `Building #${filters.buildingId}`;
});

const filterRefMap = {
	search: searchFilterRef,
	client: clientFilterRef,
	building: buildingFilterRef,
	status: statusFilterRef,
};

const startFilterEdit = (type) => {
	editingFilterType.value = type;
	nextTick(() => {
		const r = filterRefMap[type];
		if (r?.value) {
			if (typeof r.value.focus === 'function') {
				r.value.focus();
			} else if (typeof r.value.focusInput === 'function') {
				r.value.focusInput();
			}
		}
	});
};

const handleFilterValue = (key, val) => {
	handleCatalogFilterChange(key, val);
	editingFilterType.value = null;
};

const clearSearchFilter = () => {
	filters.search = '';
	editingFilterType.value = null;
	reloadItems();
};

const handleStatusSelect = () => {
	handleStatusFilterChange();
	editingFilterType.value = null;
};

const selectStatus = (value) => {
	filters.status = value;
	handleStatusFilterChange();
	editingFilterType.value = null;
};

const ensureListSelection = () => {
	const provided = toId(props.listId);
	if (provided) {
		listId.value = provided;
		return true;
	}
	if (listId.value) {
		return true;
	}
	if (clientInvoicesStore.lists.length) {
		const fallback = clientInvoicesStore.lists[0];
		const fallbackId = toId(fallback.id);
		listId.value = fallbackId;
		return true;
	}
	return false;
};

const resultsStart = computed(() => {
	if (pagination.value.total === 0) return 0;
	return (pagination.value.page - 1) * (pagination.value.pageSize || 0) + 1;
});

const resultsEnd = computed(() => {
	return Math.min(pagination.value.page * (pagination.value.pageSize || 0), pagination.value.total);
});

const visiblePages = computed(() => {
	const count = pagination.value.pageCount || 1;
	const current = pagination.value.page;
	if (count <= 7) {
		return Array.from({ length: count }, (_, i) => i + 1);
	}
	const pages = [1];
	
	let start = Math.max(2, current - 1);
	let end = Math.min(count - 1, current + 1);
	
	if (current <= 4) {
		end = 5;
		if (current === 4) end = 6;
	}
	if (current >= count - 3) {
		start = count - 4;
		if (current === count - 3) start = count - 5;
	}
	
	if (start > 2) {
		pages.push('...');
	}
	
	for (let i = start; i <= end; i++) {
		pages.push(i);
	}
	
	if (end < count - 1) {
		pages.push('...');
	}
	if (count > 1) {
		pages.push(count);
	}
	return pages;
});

// Run after a completed tap, in capture phase so child click.stop cannot leave
// stale popovers behind. Never collapse an inline editor between touchstart and click.
const handlePageClick = (event) => {
	const target = event.target;
	// Filter controls own transitions within the bar. Resetting here can flush
	// a collapsed layout before the native click reaches the next filter button.
	if (editingFilterType.value && !target.closest('.filters-bar')) {
		editingFilterType.value = null;
	}
	if (!submenuRef.value?.contains(target) && (!target.closest('.table-row') || target.closest('.col-select, .col-status'))) {
		closeSubmenu();
	}
};

watch(
	() => [filters.search, filters.status, filters.dateFrom, filters.dateTo],
	() => {
		if (syncingFilters.value) return;
		persistFiltersForCurrentList();
	},
	{ deep: false },
);

watch(
	() => props.listId,
	(newId) => {
		if (!storeReady.value) return;
		if (newId && newId !== listId.value) {
			listId.value = toId(newId);
		} else if (!newId && !listId.value) {
			ensureListSelection();
		}
	},
);

watch(
	() => clientInvoicesStore.lists.length,
	() => {
		if (!storeReady.value) return;
		if (!props.listId && !listId.value) {
			ensureListSelection();
		}
	},
);

const reloadItems = async () => {
	if (!listId.value) return;
	currentPage.value = 1;
	clearSelection();
	try {
		const filtersPayload = serializeFilters();
		await clientInvoicesStore.fetchItemsForList(listId.value, {
			filters: filtersPayload,
			page: currentPage.value,
			allPages: hasActiveFilters.value,
		});
	} catch (error) {
		logError(error);
	}
};

const changePage = (page) => {
	if (!listId.value || hasActiveFilters.value) return;
	currentPage.value = page;
	clearSelection();
	clientInvoicesStore.fetchItemsForList(listId.value, {
		filters: serializeFilters(),
		page,
	}).catch((error) => logError(error));
};

const handleCreateInvoice = () => {
	if (!listId.value) return;
	emit('open-editor', { listId: listId.value, mode: 'invoice' });
};


const getClientName = (item) => {
	return (
		item?.client?.name ||
		item?.clientSnapshot?.name ||
		item?.clientSnapshot?.company ||
		'—'
	);
};

const deleteInvoiceLabel = computed(() => {
	const target = deleteTarget.value;
	if (!target) return '';
	const number = target.invoiceNumber ? `invoice #${target.invoiceNumber}` : 'this invoice';
	const clientName = getClientName(target);
	if (clientName && clientName !== '—') {
		return `${number} for ${clientName}`;
	}
	return number;
});

const getBuildingName = (item) => {
	return (
		item?.building?.name ||
		item?.clientSnapshot?.buildingName ||
		item?.clientSnapshot?.building ||
		'—'
	);
};

const formatDate = (value) => {
	if (!value) return '—';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return date.toLocaleDateString();
};

const getPaidDate = (item) => {
	return item?.paidDate || item?.data?.paidDate || item?.attributes?.paidDate || '';
};

const handleStatusUpdate = async (item, { status, paidDate }) => {
	if (!item?.documentId) return;
	
	const currentStatus = (item.invoiceStatus || 'unpaid').toLowerCase();
	const currentPaidDate = getPaidDate(item);
	
	if (status === currentStatus && paidDate === currentPaidDate) {
		return;
	}

	statusUpdatingId.value = item.id;
	const optimisticPaidDate = status === 'paid' ? paidDate : null;
	const previousStatus = currentStatus;
	const previousPaidDate = currentPaidDate || null;
	const cachedInvoice = clientInvoicesStore.currentInvoiceCache?.[item.documentId];
	item.invoiceStatus = status;
	item.paidDate = optimisticPaidDate;
	if (cachedInvoice) {
		cachedInvoice.invoiceStatus = status;
		cachedInvoice.paidDate = optimisticPaidDate;
	}
	try {
		const payload = {
			invoiceStatus: status,
			paidDate: status === 'paid' ? paidDate : null
		};
		const saved = await clientInvoicesStore.saveInvoice({
			documentId: item.documentId,
			payload,
			refreshList: false,
		});
		if (saved) {
			item.invoiceStatus = saved.invoiceStatus || item.invoiceStatus;
			item.paidDate = saved.paidDate ?? item.paidDate;
			if (cachedInvoice) {
				cachedInvoice.invoiceStatus = item.invoiceStatus;
				cachedInvoice.paidDate = item.paidDate;
			}
		}
	} catch (error) {
		item.invoiceStatus = previousStatus;
		item.paidDate = previousPaidDate;
		if (cachedInvoice) {
			cachedInvoice.invoiceStatus = previousStatus;
			cachedInvoice.paidDate = previousPaidDate;
		}
		logError(error);
		await reloadItems();
	} finally {
		statusUpdatingId.value = null;
	}
};

const fetchFullInvoice = async (item) => {
	if (!item?.documentId) return null;
	try {
		return await clientInvoicesStore.fetchInvoiceById(item.documentId);
	} catch (e) {
		logError(e);
		return item;
	}
};

const formatCurrency = (value) => {
	if (value === null || value === undefined || value === '') return '—';
	const numeric = Number(value);
	if (Number.isNaN(numeric)) return value;
	return new Intl.NumberFormat('en-GB', {
		style: 'currency',
		currency: INVOICE_CURRENCY,
	}).format(numeric);
};

const handleCatalogFilterChange = (key, value) => {
	if (!(key in filters)) return;
	filters[key] = value || null;
	persistFiltersForCurrentList();
	reloadItems();
};

const resolveInvoiceKey = (item) => toId(item?.documentId || item?.id);

const isInvoiceSelected = (item) => {
	const key = resolveInvoiceKey(item);
	return key ? selectedInvoiceIds.value.includes(key) : false;
};

const toggleInvoiceSelection = (item) => {
	const key = resolveInvoiceKey(item);
	if (!key) return;
	if (selectedInvoiceIds.value.includes(key)) {
		selectedInvoiceIds.value = selectedInvoiceIds.value.filter((id) => id !== key);
	} else {
		selectedInvoiceIds.value = [...selectedInvoiceIds.value, key];
	}
};

const clearSelection = () => {
	selectedInvoiceIds.value = [];
};

const formatDateForExport = (value) => {
	if (!value) return '—';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return String(value);
	return date.toLocaleDateString('en-GB');
};

const toNumericAmount = (value) => {
	if (value === null || value === undefined || value === '') return null;
	if (typeof value === 'number') return Number.isFinite(value) ? value : null;
	const raw = String(value).trim();
	if (!raw) return null;
	const cleaned = raw.replace(/[^\d.,-]/g, '');
	if (!cleaned) return null;
	const lastComma = cleaned.lastIndexOf(',');
	const lastDot = cleaned.lastIndexOf('.');
	const sepIndex = Math.max(lastComma, lastDot);

	if (sepIndex !== -1) {
		const intPart = cleaned
			.slice(0, sepIndex)
			.replace(/(?!^)-/g, '')
			.replace(/[^\d-]/g, '');
		const fracPart = cleaned
			.slice(sepIndex + 1)
			.replace(/[^\d]/g, '');
		const composed = fracPart ? `${intPart}.${fracPart}` : intPart;
		const parsed = Number.parseFloat(composed);
		return Number.isFinite(parsed) ? parsed : null;
	}

	const digitsOnly = cleaned.replace(/(?!^)-/g, '').replace(/[^\d-]/g, '');
	const parsed = Number.parseFloat(digitsOnly);
	return Number.isFinite(parsed) ? parsed : null;
};

const resolveInvoiceTotal = (item) =>
	toNumericAmount(item?.totalAmount) ??
	toNumericAmount(item?.invoiceContent?.summary?.total) ??
	toNumericAmount(item?.invoiceContent?.summary?.subtotal) ??
	null;

const normalizeDocumentType = (value) => String(value || '').trim().toLowerCase();

const isCreditNoteItem = (item) => {
	if (!item) return false;
	if (normalizeDocumentType(item?.metadata?.documentType) === 'credit-note') {
		return true;
	}
	const total = resolveInvoiceTotal(item);
	return total !== null && total < 0;
};

const filteredTotals = computed(() => {
	if (!hasActiveFilters.value) return { total: null, counted: 0 };
	let total = 0;
	let counted = 0;
	items.value.forEach((item) => {
		const amount = resolveInvoiceTotal(item);
		if (amount === null) return;
		total += amount;
		counted += 1;
	});
	return { total: counted ? total : null, counted };
});

const formattedFilteredTotal = computed(() => formatCurrency(filteredTotals.value.total));

const formatCurrencyForExport = (amount) => {
	if (amount === null || amount === undefined) return '—';
	const rounded = Math.round(amount * 100) / 100;
	try {
		return new Intl.NumberFormat('en-GB', {
			style: 'currency',
			currency: INVOICE_CURRENCY,
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}).format(rounded);
	} catch (e) {
		return `£${rounded.toFixed(2)}`;
	}
};

const escapeHtml = (value) =>
	String(value ?? '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');

const buildFilterSummary = () => {
	const parts = [];
	if (filters.search) parts.push(`#${filters.search}`);

	if (filters.clientId) {
		const match = clientOptions.value.find((opt) => String(opt.id) === String(filters.clientId));
		parts.push(`Client: ${match?.name || filters.clientId}`);
	}

	if (filters.buildingId) {
		const match = buildingOptions.value.find((opt) => String(opt.id) === String(filters.buildingId));
		parts.push(`Building: ${match?.name || filters.buildingId}`);
	}

	if (filters.dateFrom && filters.dateTo && filters.dateFrom !== filters.dateTo) {
		parts.push(`Date: ${formatDateForExport(filters.dateFrom)} – ${formatDateForExport(filters.dateTo)}`);
	} else if (filters.dateFrom) {
		parts.push(`Date: ${formatDateForExport(filters.dateFrom)}`);
	}

	return parts.join(' • ');
};

const deriveExportFilename = () => {
	const base = (currentList.value?.name || 'client-invoices')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '');
	const stamp = new Date().toISOString().slice(0, 10);
	return `${base || 'client-invoices'}-statement-${stamp}.pdf`;
};

const buildExportTableHtml = (rows, summary, filterSummary = '') => {
	const title = escapeHtml('New Order Clean - Invoices Summary');
	const countDetails = filterSummary ? ` • ${filterSummary}` : '';
	const countText = `${rows.length} invoices${countDetails}`;

	const rowsMarkup = rows.length
		? rows
				.map(
					(row) => `
			<tr>
				<td class="col-number">${escapeHtml(row.invoiceNumber)}</td>
				<td class="col-date">${escapeHtml(row.invoiceDate)}</td>
				<td class="col-client">${escapeHtml(row.clientName)}</td>
				<td class="col-building">${escapeHtml(row.building || '-')}</td>
				<td class="col-total numeric">${escapeHtml(row.totalDisplay)}</td>
			</tr>`,
				)
				.join('')
		: '<tr><td colspan="5" class="empty-cell">No invoices available.</td></tr>';

	const totalLabel = 'Total';

	return `
		<style>
			.invoice-export-root {
				font-family: "Inter", Arial, sans-serif;
				color: #1f2933;
				background-color: #ffffff;
				padding: 20px 24px;
				box-sizing: border-box;
				width: 100%;
			}
			.invoice-export-header {
				display: flex;
				justify-content: space-between;
				align-items: baseline;
				gap: 16px;
			}
			.invoice-export-title {
				font-size: 20px;
				font-weight: 600;
				margin: 0;
			}
			.invoice-export-meta-count {
				font-size: 12px;
				color: #6b7280;
				margin: 4px 0 0 0;
			}
			.invoice-export-table {
				width: 100%;
				margin-top: 20px;
				font-size: 0.9rem;
				border-collapse: collapse;
				border-spacing: 0;
				border: 0.6px solid #e5e7eb;
				table-layout: fixed;
			}
			.invoice-export-table td {
				padding: 6px 8px;
				text-align: left;
			}
			.invoice-export-table tr {
				page-break-inside: avoid;
				break-inside: avoid;
			}
			.invoice-export-table tbody tr td {
				border-bottom: 0.6px solid #e5e7eb;
			}
			.invoice-export-table tbody tr:last-child td {
				border-bottom: 0;
			}
			.invoice-export-table td.numeric {
				text-align: right;
				font-weight: 600;
			}
			.invoice-export-table tbody tr:nth-of-type(even) {
				background-color: #f9fafb;
			}
			.invoice-export-table .empty-cell {
				text-align: center;
				padding: 32px 12px;
				font-style: italic;
				color: #6b7280;
			}
			.invoice-export-table .col-number { width: 50px; font-weight: 400; }
			.invoice-export-table .col-date { width: 100px; }
			.invoice-export-table .col-client { width: 150px; }
			.invoice-export-table .col-building { width: auto; }
			.invoice-export-table .col-total { width: 100px; font-weight: 600; }
			.invoice-export-table tfoot .summary-row td {
				font-weight: 600;
				font-size: 0.9rem;
				background-color: #f3f4f6;
				border-top: 0.8px solid #d1d5db;
			}
			.summary-row .summary-label {
				text-align: right;
				padding-right: 8px;
			}
			.summary-row .summary-value {
				text-align: right;
			}
		</style>
		<div class="invoice-export-root">
			<div class="invoice-export-header">
				<div>
					<h1 class="invoice-export-title">${title}</h1>
					<p class="invoice-export-meta-count">${escapeHtml(countText)}</p>
				</div>
			</div>
			<table class="invoice-export-table">
				<tbody>
					${rowsMarkup}
				</tbody>
				<tfoot>
					<tr class="summary-row">
						<td colspan="4" class="summary-label">${escapeHtml(totalLabel)}</td>
						<td class="summary-value">${escapeHtml(summary.formattedTotal)}</td>
					</tr>
				</tfoot>
			</table>
		</div>
	`;
};

const waitForImages = async (root) => {
	if (!root) return;
	const imgs = Array.from(root.querySelectorAll('img'));
	if (!imgs.length) return;
	await Promise.all(
		imgs.map(
			(img) =>
				new Promise((resolve) => {
					if (img.complete && img.naturalWidth !== 0) {
						resolve();
						return;
					}
					const onComplete = () => {
						img.removeEventListener('load', onComplete);
						img.removeEventListener('error', onComplete);
						resolve();
					};
					img.addEventListener('load', onComplete);
					img.addEventListener('error', onComplete);
					setTimeout(onComplete, 1000);
				}),
		),
	);
};

const downloadBinaryBytes = (bytes, filename, mimeType) => {
	const blob = new Blob([bytes], { type: mimeType });
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = filename;
	link.click();
	URL.revokeObjectURL(url);
};

const downloadPdfBytes = (pdfBytes, filename) =>
	downloadBinaryBytes(pdfBytes, filename, 'application/pdf');

const compressPdfBytes = async (pdfBytes) => {
	const { PDFDocument } = await import('pdf-lib');
	const input = pdfBytes instanceof ArrayBuffer ? new Uint8Array(pdfBytes) : pdfBytes;
	const pdfDoc = await PDFDocument.load(input, { ignoreEncryption: true });
	return pdfDoc.save({
		useObjectStreams: true,
		updateFieldAppearances: false,
		subsetFonts: true,
	});
};

const buildInvoicePdfBytes = async (html) => {
	if (!html) {
		throw new Error('Missing invoice HTML');
	}
	const { default: html2pdf } = await import('html2pdf.js').catch(() => ({ default: null }));
	if (!html2pdf) {
		throw new Error('PDF generator not available. Please install html2pdf.js');
	}
	const exportScale = Math.max(2.5, window.devicePixelRatio || 1);
	const container = document.createElement('div');
	container.style.position = 'absolute';
	container.style.left = '-9999px';
	container.style.width = '8.27in';
	container.style.height = '11.68in';
	container.innerHTML = html;
	document.body.appendChild(container);

	try {
		const el =
			container.querySelector('.pdf-gen-invoice-document') ||
			container.querySelector('.pdf-gen-invoice-container') ||
			container;
		await waitForImages(el);
		const worker = html2pdf().from(el).set({
			margin: 0,
			image: { type: 'jpeg', quality: 0.85 },
			html2canvas: { scale: exportScale, useCORS: true, onclone: waitForClonedDocumentFonts },
			jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait', compress: true },
			pagebreak: { mode: ['css', 'legacy'] },
		});

		const pdfArrayBuffer = await worker.outputPdf('arraybuffer');
		if (!pdfArrayBuffer) {
			throw new Error('PDF generation failed');
		}

		let compressedPdfBytes = pdfArrayBuffer;
			try {
				compressedPdfBytes = await compressPdfBytes(pdfArrayBuffer);
			} catch (compressionError) {
				logError(compressionError);
			}
			return compressedPdfBytes;
		} finally {
			if (container.parentNode) {
			container.parentNode.removeChild(container);
		}
	}
};

const downloadInvoicePdfFromHtml = async ({ html, filename }) => {
	const pdfBytes = await buildInvoicePdfBytes(html);
	downloadPdfBytes(pdfBytes, filename);
};

const fetchAllInvoicesForExport = async () => {
	const exportPageSize = 200;
	const allItems = [];
	let page = 1;
	const MAX_ITERATIONS = 500;
	const filtersPayload = serializeFilters();

	while (page <= MAX_ITERATIONS) {
		const { items: pageItems = [], pagination: meta = {} } = await clientInvoicesStore.fetchItemsSnapshot(
			listId.value,
			{
				filters: filtersPayload,
				page,
				pageSize: exportPageSize,
				sort: ['invoiceDate:desc', 'invoiceNumber:desc'],
			},
		);

		if (Array.isArray(pageItems) && pageItems.length) {
			allItems.push(...pageItems);
		}

		const pageCount = meta?.pageCount;
		if (pageCount && page >= pageCount) {
			break;
		}
		if ((!pageCount || pageCount < 1) && (!pageItems || pageItems.length < exportPageSize)) {
			break;
		}
		page += 1;
	}

	return allItems;
};

const exportInvoicesTable = async () => {
	if (!listId.value || isExporting.value) return;
	isExporting.value = true;

	try {
		toast?.info?.('Preparing export...');
		const itemsForExport = await fetchAllInvoicesForExport();
		if (!Array.isArray(itemsForExport) || itemsForExport.length === 0) {
			toast?.info?.('No invoices to export for current filters.');
			return;
		}

		const exportRows = itemsForExport.map((item) => {
			const numericTotal =
				toNumericAmount(item?.totalAmount) ??
				toNumericAmount(item?.invoiceContent?.summary?.total) ??
				toNumericAmount(item?.invoiceContent?.summary?.subtotal) ??
				null;
			const fallbackTotal =
				item?.totalAmount ??
				item?.invoiceContent?.summary?.total ??
				item?.invoiceContent?.summary?.subtotal ??
				'';
			
			const rawInvoiceNumber = item.invoiceNumber || '';
			const invoiceNumberDisplay = rawInvoiceNumber ? `#${rawInvoiceNumber}` : '#-';

			return {
				invoiceNumber: invoiceNumberDisplay,
				invoiceDate: formatDateForExport(item.invoiceDate),
				clientName: getClientName(item),
				building: getBuildingName(item),
				totalDisplay: numericTotal !== null ? formatCurrencyForExport(numericTotal) : (fallbackTotal || '-'),
				numericTotal,
			};
		});

		const numericRows = exportRows.filter((row) => row.numericTotal !== null);
		const totalAmount = numericRows.reduce((sum, row) => sum + (row.numericTotal || 0), 0);
		const summary = {
			totalAmount,
			formattedTotal: numericRows.length ? formatCurrencyForExport(totalAmount) : '-',
			counted: numericRows.length,
			totalRows: exportRows.length,
			missingCount: exportRows.length - numericRows.length,
		};

		let filterSummary = '';
		if (filters.dateFrom && filters.dateTo && filters.dateFrom !== filters.dateTo) {
			filterSummary = `${formatDateForExport(filters.dateFrom)} – ${formatDateForExport(filters.dateTo)}`;
		} else if (filters.dateFrom) {
			filterSummary = formatDateForExport(filters.dateFrom);
		}

		const htmlContent = buildExportTableHtml(exportRows, summary, filterSummary);
		const container = document.createElement('div');
		container.style.position = 'fixed';
		container.style.left = '-9999px';
		container.style.top = '0';
		container.style.width = '290mm'; // A4 Landscape width (approx 297mm - margins)
		container.innerHTML = htmlContent;
		document.body.appendChild(container);

		try {
			const exportRoot = container.querySelector('.invoice-export-root');
			if (!exportRoot) throw new Error('Export root element not found');

			const { default: html2pdf } = await import('html2pdf.js').catch(() => ({ default: null }));
			if (!html2pdf) {
				throw new Error('html2pdf is not available');
			}

			const filename = deriveExportFilename();
			const exportScale = Math.max(2, window.devicePixelRatio || 1);
			const opt = {
				margin: [0.5, 0.5, 0.5, 0.5],
				filename,
				image: { type: 'jpeg', quality: 0.75 },
				html2canvas: {
					scale: exportScale,
					useCORS: true,
					backgroundColor: '#ffffff',
					onclone: waitForClonedDocumentFonts,
				},
				jsPDF: {
					unit: 'in',
					format: 'a4',
					orientation: 'landscape',
					compress: true,
				},
			};

			await waitForImages(exportRoot);
			const worker = html2pdf().from(exportRoot).set(opt);
			const pdfArrayBuffer = await worker.outputPdf('arraybuffer');
			if (!pdfArrayBuffer) {
				throw new Error('PDF generation failed');
			}

			let compressedPdfBytes = pdfArrayBuffer;
			try {
				compressedPdfBytes = await compressPdfBytes(pdfArrayBuffer);
			} catch (compressionError) {
				logError(compressionError);
			}

			downloadPdfBytes(compressedPdfBytes, filename);
			toast?.success?.('Invoice table exported.');
		} finally {
			if (container.parentNode) {
				container.parentNode.removeChild(container);
			}
		}
	} catch (error) {
		logError(error);
		toast?.error?.('Failed to export invoice table. Please try again.');
	} finally {
		isExporting.value = false;
	}
};

// Submenu Logic
const toggleSubmenu = (item, event) => {
	if (event) {
		event.stopPropagation();
		if (event.target.closest('.submenu-btn')) {
			return;
		}
	}
	
	if (activeSubmenuItemId.value === item.id) {
		closeSubmenu();
		return;
	}
	
	activeSubmenuItemId.value = item.id;
	// Measure after the menu mounts, including the larger touch targets.
	nextTick(() => {
		calculateSubmenuPosition(event);
	});
};

const openSubmenu = (item, event) => {
	if (event) {
		event.preventDefault();
		event.stopPropagation();
		if (event.target.closest('.submenu-btn')) {
			return;
		}
	}

	activeSubmenuItemId.value = item.id;
	nextTick(() => {
		calculateSubmenuPosition(event);
	});
};

const closeSubmenu = () => {
	activeSubmenuItemId.value = null;
};

const calculateSubmenuPosition = (event) => {
	if (event) submenuPoint = { left: event.clientX || 0, top: event.clientY || 0, bottom: event.clientY || 0 };
	if (!submenuRef.value) return;
	submenuStyle.value = {
		...fitFloatingPanel(submenuPoint, {
			width: submenuRef.value.getBoundingClientRect().width,
			height: submenuRef.value.scrollHeight,
		}),
		width: 'max-content',
	};
};
const handleViewportChange = () => calculateSubmenuPosition();

const findActiveItem = () => {
	return activeSubmenuItem.value;
};

const cancelDelete = () => {
	if (isDeletingInvoice.value) return;
	showDeleteModal.value = false;
	deleteTarget.value = null;
};

const confirmDelete = async () => {
	if (!deleteTarget.value || isDeletingInvoice.value) return;

	isDeletingInvoice.value = true;
	try {
		await clientInvoicesStore.deleteInvoice({
			listId: listId.value,
			documentId: deleteTarget.value.documentId,
		});
		await reloadItems();
		showDeleteModal.value = false;
		deleteTarget.value = null;
	} catch (e) {
		logError(e);
	} finally {
		isDeletingInvoice.value = false;
	}
};

const handleEditSubmenuItem = () => {
	const item = findActiveItem();
	closeSubmenu();
	if (!item) return;
	
	// Open existing invoice in editor
	emit('open-invoice', {
		listId: listId.value,
		documentId: item.documentId,
		invoice: item,
	});
};

const handleCreateCreditNoteSubmenuItem = () => {
	const item = findActiveItem();
	closeSubmenu();
	if (!item?.documentId) return;
	if (isCreditNoteItem(item)) {
		toast?.info?.('Credit notes cannot be created from another credit note.');
		return;
	}

	emit('open-editor', {
		listId: listId.value,
		mode: 'credit-note',
		sourceDocumentId: item.documentId,
		sourceInvoiceNumber: item.invoiceNumber || null,
	});
};

const handlePreviewSubmenuItem = async () => {
	const item = findActiveItem();
	closeSubmenu();
	if (!item || isGeneratingDocument.value) return;
	
	try {
		isGeneratingDocument.value = true;
		const full = await fetchFullInvoice(item);
		if (!full) return;
		const logoUrl = (await getBusinessLogoUrl()) || full.clientSnapshot?.logoUrl || null;
		const { html } = await generateInvoiceHtmlWithAutoSpacing({
			invoice: full,
			projectInfo: projectInfoData.value || {},
			snapshot: full.clientSnapshot || {},
			logoUrl,
		});
		openPreviewModal(full, html);
	} catch (error) {
		logError(error);
	} finally {
		isGeneratingDocument.value = false;
	}
};

const handleDownloadSubmenuItem = async () => {
	const item = findActiveItem();
	closeSubmenu();
	if (!item || isGeneratingDocument.value) return;

	try {
		isGeneratingDocument.value = true;
		const full = await fetchFullInvoice(item);
		if (!full) return;
		const logoUrl = (await getBusinessLogoUrl()) || full.clientSnapshot?.logoUrl || null;
		
		const { html } = await generateInvoiceHtmlWithAutoSpacing({
			invoice: full,
			projectInfo: projectInfoData.value || {},
			snapshot: full.clientSnapshot || {},
			logoUrl,
		});
		const filename = deriveInvoiceFilename(full);
		await downloadInvoicePdfFromHtml({ html, filename });
	} catch (e) {
		logError(e);
	} finally {
		isGeneratingDocument.value = false;
	}
};

const handleDownloadSelected = async () => {
	if (!hasSelection.value || isGeneratingDocument.value || isBatchDownloading.value) return;
	const selectedItems = items.value.filter((item) => isInvoiceSelected(item));
	if (!selectedItems.length) return;

	isBatchDownloading.value = true;
	const failures = [];
	try {
		const sharedLogoUrl = await getBusinessLogoUrl();
		for (const item of selectedItems) {
			const full = await fetchFullInvoice(item);
			if (!full) {
				failures.push(item);
				continue;
			}
			const logoUrl = sharedLogoUrl || full.clientSnapshot?.logoUrl || null;
			const { html } = await generateInvoiceHtmlWithAutoSpacing({
				invoice: full,
				projectInfo: projectInfoData.value || {},
				snapshot: full.clientSnapshot || {},
				logoUrl,
			});
			const filename = deriveInvoiceFilename(full);
			await downloadInvoicePdfFromHtml({ html, filename });
		}
		if (failures.length) {
			toast?.error?.(`Failed to download ${failures.length} invoice${failures.length === 1 ? '' : 's'}.`);
		}
	} catch (error) {
		logError(error);
		toast?.error?.('Failed to download selected invoices.');
	} finally {
		isBatchDownloading.value = false;
	}
};

const handleDeleteSubmenuItem = async () => {
	const item = findActiveItem();
	closeSubmenu();
	if (!item || !item.documentId) return;

	deleteTarget.value = item;
	showDeleteModal.value = true;
};

watch(
	() => listId.value,
	async (newId, oldId) => {
		if (!newId || !storeReady.value || newId === oldId) {
			return;
		}
		clearSelection();
		await bootstrapCurrentList();
	},
);

onMounted(async () => {
	document.addEventListener('click', handlePageClick, true);
	window.addEventListener('resize', handleViewportChange);
	window.visualViewport?.addEventListener('resize', handleViewportChange);
	window.visualViewport?.addEventListener('scroll', handleViewportChange);
	try {
		await projectsStore.ensureInitialized?.();
		await clientInvoicesStore.initialize();
		await catalogStore.bootstrap?.();
	} catch (error) {
		logError(error);
	} finally {
		storeReady.value = true;
		ensureListSelection();
		if (listId.value) {
			await bootstrapCurrentList();
		}
	}
});
onBeforeUnmount(() => {
	document.removeEventListener('click', handlePageClick, true);
	window.removeEventListener('resize', handleViewportChange);
	window.visualViewport?.removeEventListener('resize', handleViewportChange);
	window.visualViewport?.removeEventListener('scroll', handleViewportChange);
});
onDeactivated(() => {
	closeSubmenu();
	editingFilterType.value = null;
});

// Invoices added or changed on another device show up here without reloading. The page on
// screen is read again quietly, and only replaced while nothing on it is open or selected.
const isInvoiceListBusy = () =>
	!storeReady.value ||
	isLoading.value ||
	isExporting.value ||
	Boolean(statusEditorOpenItemId.value || activeSubmenuItemId.value || editingFilterType.value) ||
	selectedInvoiceIds.value.length > 0;

const refreshInvoicesQuietly = async ({ canApply }) => {
	if (!listId.value || isInvoiceListBusy()) return;
	const requestedList = listId.value;
	const requestedPage = currentPage.value;
	const filtersKey = JSON.stringify(clientInvoicesStore.getActiveFilters(requestedList));
	const result = await clientInvoicesStore.peekItemsForList(requestedList, {
		page: requestedPage,
		allPages: hasActiveFilters.value,
	});
	// The list, page or filters changed while the request was out, or something was opened.
	if (!result || listId.value !== requestedList || currentPage.value !== requestedPage) return;
	if (JSON.stringify(clientInvoicesStore.getActiveFilters(requestedList)) !== filtersKey) return;
	if (!canApply() || isInvoiceListBusy()) return;
	if (sameRecords(items.value, result.items)) return;
	clientInvoicesStore.$patch((state) => {
		state.itemsByListId[requestedList] = result.items;
		state.paginationByListId[requestedList] = result.pagination;
	});
};

useLiveRefresh(refreshInvoicesQuietly, { isBusy: isInvoiceListBusy });
</script>

<style scoped>
.client-invoices-list {
	--ap-type-control: 500 0.875rem/1.25rem var(--font-family-base);
	--ap-type-body: 400 0.875rem/1.25rem var(--font-family-base);
	--ap-type-caption: 400 0.8125rem/1.125rem var(--font-family-base);
	--ap-type-table-label: 500 0.8125rem/1.125rem var(--font-family-base);
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding: 24px;
	color: var(--text-color);
	/* height: 100%; removed */
}

/* Invoice controls row */
.invoice-controls {
	display: flex;
	align-items: center;
	gap: 12px;
	flex-wrap: wrap;
}

/* Independent controls, matching the journal action buttons. */
.action-bar {
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
}

.action-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	height: 34px;
	padding: 0 13px;
	border: 1px solid var(--border-color);
	background: var(--noc-background, #fff);
	border-radius: 8px;
	box-shadow: none;
	color: var(--text-color);
	font: 500 0.875rem/1.25rem var(--font-family-base);
	white-space: nowrap;
	cursor: pointer;
	transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.action-btn:hover:not(:disabled) {
	background-color: color-mix(in srgb, var(--text-color-secondary) 4%, transparent);
}

.action-btn:focus-visible {
	outline: 2px solid rgba(0, 101, 255, 0.28);
	outline-offset: 1px;
}

.action-btn:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.action-btn--muted {
	color: var(--text-color-secondary, #6b7280);
}

.action-btn--muted:hover:not(:disabled) {
	background: var(--noc-background-secondary, #f5f5f5);
	color: var(--text-color, #374151);
}

.action-btn-icon {
	font-size: 1.05rem;
	width: 1.05rem;
	height: 1.05rem;
	flex-shrink: 0;
}

.action-bar-divider {
	width: 1px;
	height: 20px;
	margin: 0 4px;
	background: var(--border-color, #e5e7eb);
}

/* Shared journal filter styling. */
.filters-bar {
	display: flex;
	align-items: center;
	gap: 8px;
	flex: 1 1 320px;
	min-width: 0;
	max-width: 100%;
	min-height: 36px;
	padding: 2px 12px;
	box-sizing: border-box;
	flex-wrap: wrap;
	position: relative;
	background: var(--noc-background, #fff);
	z-index: 40;
	border: 1px solid var(--border-color);
	border-radius: 8px;
	box-shadow: none;
}

.filters-bar.is-disabled {
	opacity: 0.7;
	pointer-events: none;
}

.filters-bar-start {
	display: flex;
	align-items: center;
	color: var(--text-color-secondary);
}

.filter-chip {
	display: inline-flex;
	align-items: center;
	gap: 2px;
	height: 30px;
	padding: 0 4px 0 10px;
	border: 1px solid var(--border-color);
	border-radius: 6px;
	background: var(--noc-background);
	font: var(--ap-type-control);
	color: var(--text-color);
	white-space: nowrap;
	box-shadow: none;
}

.filter-chip-text {
	max-width: 240px;
	overflow: hidden;
	text-overflow: ellipsis;
	line-height: inherit;
}

.filter-chip-remove {
	display: flex;
	align-items: center;
	justify-content: center;
	background: none;
	border: none;
	cursor: pointer;
	color: var(--text-color-secondary, #9ca3af);
	padding: 4px;
	border-radius: 4px;
	font-size: 0.8rem;
	transition: color 0.15s, background-color 0.15s;
}

.filter-chip-remove:hover:not(:disabled) {
	background-color: color-mix(in srgb, var(--text-color-secondary) 4%, transparent);
}

.filter-label-btn {
	display: inline-flex;
	align-items: center;
	height: 30px;
	padding: 0 10px;
	border: none;
	background: none;
	color: var(--text-color-secondary);
	font: var(--ap-type-control);
	cursor: pointer;
	border-radius: 6px;
	white-space: nowrap;
}

.filter-label-btn:hover:not(:disabled) {
	background-color: color-mix(in srgb, var(--text-color-secondary) 4%, transparent);
}

.filter-inline-editor {
	position: relative;
	width: 100%;
	min-width: 0;
}

.filter-editor-search {
	width: 100%;
}

.filter-editor-search :deep(.field-input) {
	border: 1px solid var(--border-color);
	border-radius: 6px;
	padding: 6px 10px;
	font: var(--ap-type-body);
	height: 30px;
}

.filter-editor-search :deep(.field-input):focus {
	border-color: var(--noc-blue, #0065ff);
}

.filter-editor-input {
	height: 30px;
	border: 1px solid var(--border-color, #dcdcdc);
	border-radius: 6px;
	padding: 6px 10px;
	font: var(--ap-type-body);
	outline: none;
	background: var(--noc-background, #fff);
	color: var(--text-color, #333);
	width: 100%;
	box-sizing: border-box;
}

.filter-editor-input:focus {
	border-color: var(--noc-blue);
}

.status-dropdown-wrapper {
	position: relative;
}

.status-dropdown {
	position: absolute;
	top: calc(100% + 6px);
	left: 0;
	min-width: 120px;
	background: var(--noc-background, #fff);
	border: 1px solid var(--border-color, #e5e7eb);
	border-radius: 8px;
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
	padding: 4px;
	z-index: 100;
	display: flex;
	flex-direction: column;
}

.status-dropdown-option {
	display: block;
	width: 100%;
	padding: 8px 12px;
	background: none;
	border: none;
	text-align: left;
	font: var(--ap-type-control);
	color: var(--text-color, #333);
	cursor: pointer;
	border-radius: 6px;
	transition: background-color 0.15s;
}

.status-dropdown-option:hover {
	background: var(--noc-background-secondary, #f5f5f5);
}

.filter-date-wrapper {
	display: inline-flex;
	align-items: center;
	gap: 2px;
	max-width: 100%;
	border: 1px solid transparent;
	border-radius: 6px;
	box-sizing: border-box;
}

.filter-date-wrapper.has-value {
	border-color: var(--border-color);
}

.filter-date-wrapper :deep(.date-range-picker) {
	min-width: 0;
	max-width: 100%;
}

.filter-date-wrapper :deep(.drp-trigger) {
	border: none;
	background: transparent;
	padding: 0 10px;
	height: 28px;
	font: var(--ap-type-control);
	color: var(--text-color-secondary);
	border-radius: 6px;
	box-shadow: none;
	font-variant-numeric: tabular-nums;
}

.filter-date-wrapper :deep(.drp-trigger:hover:not(:disabled)) {
	background-color: color-mix(in srgb, var(--text-color-secondary) 4%, transparent);
}

.filter-date-wrapper :deep(.drp-trigger--active) {
	color: var(--text-color);
}

.filter-date-wrapper :deep(.drp-trigger-text) {
	color: inherit;
}

/* Match the flat journal tables. Keep status popovers outside the table bounds. */
.content-wrapper {
	display: flex;
	flex-direction: column;
	min-width: 0;
}

.table-wrapper {
	background: transparent;
	border: none;
	box-shadow: none;
	border-radius: 0;
	/* Keep status popovers from being clipped by the table container. */
	overflow: visible;
	display: flex;
	flex-direction: column;
	position: relative;
}

.invoice-list-container {
	width: 100%;
	min-width: 0;
	overflow-x: auto;
	-webkit-overflow-scrolling: touch;
}

.invoice-table {
	width: 100%;
	box-sizing: border-box;
	min-width: 1100px;
	max-width: 100%;
	table-layout: fixed;
	border-collapse: separate;
	border-spacing: 0;
	border-inline: 1px solid var(--border-color);
	font: var(--ap-type-body);
}

.invoice-table th,
.invoice-table td {
	padding: 8px 16px;
	border-bottom: 1px solid var(--border-color);
	text-align: left;
	vertical-align: middle;
}

.invoice-table th:first-child,
.invoice-table td:first-child {
	padding-left: 16px;
}

.invoice-table th:last-child,
.invoice-table td:last-child {
	padding-right: 16px;
}

.invoice-table tbody {
	background: var(--noc-background);
}

.invoice-table tbody tr:not(:last-child) td:first-child {
	border-image: linear-gradient(to right, transparent 16px, var(--border-color) 16px) 1;
}

.invoice-table th {
	background-color: #fafafa;
	border-top: 1px solid var(--border-color);
	font: var(--ap-type-table-label);
	font-weight: 600;
	color: var(--text-color);
	position: sticky;
	top: 0;
	z-index: 1;
}

/* Column Widths */
.col-select {
	width: 44px;
	text-align: center;
}
.col-select .row-select-checkbox {
	width: 16px;
	height: 16px;
	margin: 0;
	vertical-align: middle;
	accent-color: var(--noc-blue);
	cursor: pointer;
}
.invoice-table td.col-select {
	padding-left: 16px;
	padding-right: 8px;
}
.col-number { width: 100px; font-weight: 600; }
.col-date { width: 120px; }
.col-client {
	width: 160px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
.col-building {
	min-width: 150px;
	max-width: 400px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
.col-due { width: 120px; }
.col-total { width: 120px; font-weight: 600; }
.col-status {
	width: 101px;
	text-align: center;
}
.invoice-table th.col-status {
	padding: 8px 0;
	text-align: center;
}
.invoice-table td.col-status {
	padding: 0;
}

.status-cell-wrapper {
	position: relative;
	overflow: visible;
}

.status-cell-wrapper.is-open {
	z-index: 9999;
}

.col-status :deep(.status-cell) {
	min-width: auto;
	align-items: center;
}

.col-status :deep(.status-cell.is-open) {
	z-index: 9999;
}

td.col-status:has(.is-open) {
	position: relative;
	z-index: 9999;
}

.table-row {
	height: 42px;
	cursor: pointer;
	transition: background-color 0.15s;
}

/* Compact rows on every screen; an iPad shows the same table as a desktop. */
.table-row { height: 38px; }
.invoice-table .table-row > td:not(.col-status) { padding-block: 6px; }
.col-status :deep(.status-trigger) { min-height: 36px; padding-block: 0; }

.table-row.status-editor-open {
	position: relative;
	z-index: 50;
}

.table-row.active {
	background: var(--noc-blue-05);
}

@media (hover: hover) {
	.table-row:hover:not(.active) {
		background: color-mix(in srgb, var(--text-color-secondary) 4%, transparent);
	}
}

.invoice-table .numeric,
.invoice-table th.col-total {
	text-align: right;
}

.col-date,
.col-due,
.col-total {
	font-variant-numeric: tabular-nums;
}

.invoice-total-row {
	min-width: 1100px;
	display: table;
	table-layout: fixed;
	width: 100%;
	margin-top: 6px;
}

.invoice-total-row .total-cell {
	display: table-cell;
	padding: 8px 16px;
	font: var(--ap-type-body);
	vertical-align: middle;
	height: 40.5px;
}

.invoice-total-row .total-cell:first-child {
	padding-left: 24px;
}

.invoice-total-row .total-cell:last-child {
	padding-right: 48px;
}

.invoice-total-row .total-cell.col-select {
	width: 44px;
}

.invoice-total-row .total-cell.col-number {
	width: 100px;
}

.invoice-total-row .total-cell.col-date {
	width: 120px;
}

.invoice-total-row .total-cell.col-client {
	width: 160px;
}

.invoice-total-row .total-cell.col-building {
	width: auto;
}

.invoice-total-row .total-cell.col-due {
	width: 120px;
}

.invoice-total-row .total-cell.col-total {
	width: 120px;
	text-align: left;
	font-weight: 600;
	color: var(--text-color);
	white-space: nowrap;
}

.invoice-total-row .total-cell.col-status {
	width: 101px;
}

.invoice-total-row .total-cell.items-count {
	text-align: right;
	color: var(--text-color-secondary);
	font-weight: 500;
	white-space: nowrap;
}

.empty-cell {
	text-align: center;
	color: var(--text-color-secondary);
	padding: 32px;
}

.loading-state,
.error-state {
	padding: 32px;
	text-align: center;
	color: var(--text-color-secondary);
}

.table-loading-overlay {
	position: absolute;
	inset: 0;
	background: rgba(255, 255, 255, 0.82);
	z-index: 20;
	pointer-events: auto;
}

.table-loading-indicator {
	position: absolute;
	top: 10px;
	left: 50%;
	transform: translateX(-50%);
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 6px 10px;
	border-radius: 999px;
	border: 1px solid var(--border-color, #dcdcdc);
	background: rgba(255, 255, 255, 0.96);
	color: var(--text-color-secondary, #4b5563);
	font-size: 0.78rem;
	font-weight: 600;
	letter-spacing: 0.01em;
}

.table-loading-spinner {
	width: 14px;
	height: 14px;
	border-radius: 50%;
	border: 2px solid #d3d7df;
	border-top-color: var(--noc-blue, #0065ff);
	animation: table-loading-spin 0.6s linear infinite;
}

@keyframes table-loading-spin {
	to {
		transform: rotate(360deg);
	}
}

.link-btn {
	border: none;
	background: none;
	color: var(--noc-blue);
	cursor: pointer;
	text-decoration: underline;
}


.pagination-bar {
	display: flex;
	align-items: center;
	justify-content: flex-start;
	padding: 4px 16px;
	margin-top: 12px;
	border-radius: 8px;
	background: transparent;
	border: none;
	box-shadow: none;
	flex-wrap: wrap;
	gap: 16px;
}

.pagination-results {
	font: var(--ap-type-caption);
	font-variant-numeric: tabular-nums;
	color: var(--text-color-secondary);
	white-space: nowrap;
}

.pagination-controls {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 8px;
}

.pagination-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 34px;
	height: 34px;
	padding: 0 8px;
	border: 1px solid var(--border-color);
	background: var(--noc-background);
	border-radius: 8px;
	color: var(--text-color);
	font: var(--ap-type-control);
	font-variant-numeric: tabular-nums;
	cursor: pointer;
	box-shadow: none;
	transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.pagination-btn:hover:not(:disabled):not(.active) {
	background-color: color-mix(in srgb, var(--text-color-secondary) 4%, transparent);
}

.pagination-btn.active {
	background-color: var(--noc-blue-05);
	border-color: var(--noc-blue-15);
	color: var(--noc-blue);
	cursor: default;
}

.pagination-btn:disabled {
	opacity: 0.4;
	cursor: not-allowed;
}

.pagination-btn:focus-visible,
.row-select-checkbox:focus-visible {
	outline: 2px solid var(--noc-blue);
	outline-offset: 2px;
}

.pagination-btn :deep(svg) {
	width: 18px;
	height: 18px;
}

.pagination-ellipsis {
	color: var(--text-color-secondary, #999);
	padding: 0 4px;
}

.empty-state {
	border: 1px dashed var(--border-color);
	border-radius: 8px;
	padding: 32px;
	text-align: center;
	color: var(--text-color-secondary);
}

.hint {
	color: var(--text-color-secondary);
	font-size: 0.85rem;
	margin-top: 8px;
}

/* Submenu Styles */
.submenu {
	font-family: var(--font-family-base);
	position: fixed;
	box-sizing: border-box;
	overflow-y: auto;
	overscroll-behavior: contain;
	background-color: var(--noc-background);
	border: 1px solid var(--border-color);
	border-radius: 4px;
	z-index: 1000;
	width: max-content;
	max-width: calc(100vw - 16px);
	padding: 6px 0;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.submenu-btn {
	display: block;
	width: 100%;
	padding: 10px 16px;
	background: none;
	border: none;
	color: var(--text-color);
	text-align: left;
	font-size: 0.9rem;
	font-family: inherit;
	cursor: pointer;
	white-space: nowrap;
	font-weight: 500;
}

.submenu-btn:hover {
	background-color: var(--noc-blue-10);
	color: var(--noc-blue);
}

.submenu-btn.delete {
	color: #dc2626; /* noc-red approx */
}

.submenu-btn.delete:hover {
	background-color: #fee2e2; /* noc-red-10 approx */
}

/* Delete confirmation modal */
.modal-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1000;
}

.modal-content {
	background: var(--noc-background);
	border-radius: 8px;
	border: 1px solid var(--border-color);
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
	padding: 24px;
	min-width: 320px;
	max-width: 90%;
	display: flex;
	flex-direction: column;
	gap: 16px;
}

.modal-title {
	margin: 0;
	font-size: 1.25rem;
	font-weight: 600;
	color: var(--text-color);
}

.modal-message {
	margin: 0;
	font-size: 0.95rem;
	color: var(--text-color-secondary);
	line-height: 1.5;
}

.modal-actions {
	display: flex;
	gap: 12px;
	justify-content: flex-end;
	margin-top: 8px;
}

.modal-btn {
	padding: 8px 20px;
	border-radius: 4px;
	border: none;
	font-weight: 600;
	cursor: pointer;
	transition: opacity 0.15s ease, background 0.15s ease;
	font-size: 0.8rem;
}

.modal-btn.cancel {
	background: transparent;
	color: var(--text-color-secondary);
	border: 1px solid var(--border-color);
}

.modal-btn.cancel:hover {
	background: var(--noc-background-secondary);
	color: var(--text-color);
}

.modal-btn.confirm {
	background: var(--noc-red);
	color: #fff;
}

.modal-btn.confirm:hover:not(:disabled) {
	background: var(--noc-red-80);
}

.modal-btn.confirm:disabled {
	opacity: 1;
}
</style>
